import React from 'react';
import { Cpu, Bell, BarChart3, Settings, User, Cloud, Database } from 'lucide-react';

// Unique Platform Loader - Main Application Loading Screen
export const PlatformLoader: React.FC = () => {
  const [progress, setProgress] = React.useState(0);
  const isPlatformStage = progress >= 40;
  const LoaderStageIcon = isPlatformStage ? Cpu : Database;
  const loaderMessage = isPlatformStage
    ? 'Initializing your intelligent operations platform...'
    : 'Preparing data connections and dashboards...';
  const ringRadius = 66;
  const ringCircumference = 2 * Math.PI * ringRadius;
  const ringOffset = ringCircumference - (progress / 100) * ringCircumference;
  const dataConnectorProgress = Math.min((progress / 40) * 100, 100);
  const platformConnectorProgress = Math.min(Math.max((progress - 40) / 60, 0) * 100, 100);
  const isDashboardStage = progress >= 72;

  React.useEffect(() => {
    const interval = window.setInterval(() => {
      setProgress((currentProgress) => {
        if (currentProgress >= 100) return 0;
        const increment = currentProgress < 40 ? 4 : 2;
        return Math.min(currentProgress + increment, 100);
      });
    }, 180);

    return () => window.clearInterval(interval);
  }, []);

  return (
    <div
      className="relative min-h-screen overflow-hidden flex items-center justify-center"
      style={{
        background:
          'radial-gradient(circle at 78% 24%, rgba(139,58,66,0.30) 0%, rgba(139,58,66,0.10) 30%, transparent 54%), radial-gradient(circle at 18% 22%, rgba(92,39,81,0.36) 0%, transparent 44%), radial-gradient(circle at 82% 92%, rgba(107,26,26,0.42) 0%, transparent 48%), linear-gradient(135deg, #130b15 0%, #2d0f1e 42%, #4a1532 70%, #5a1824 100%)',
      }}
    >
      {/* Decorative background only; loader behavior is not tied to these visual layers. */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_0%,rgba(15,8,18,0.34)_100%)]"></div>
      <div className="absolute -top-32 -right-20 h-96 w-96 rounded-full bg-secondary-500/[0.12] blur-3xl"></div>
      <div className="absolute -bottom-32 -left-24 h-[28rem] w-[28rem] rounded-full bg-primary-500/[0.12] blur-3xl"></div>
      <div className="absolute inset-x-0 bottom-0 h-[34vh] bg-gradient-to-t from-[#100811]/75 via-[#240d1b]/38 to-transparent"></div>
      <svg
        className="absolute inset-x-0 bottom-0 h-[36vh] w-full opacity-[0.46]"
        viewBox="0 0 1440 320"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id="platformLoaderWave" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor="#f9a8d4" stopOpacity="0" />
            <stop offset="45%" stopColor="#f9a8d4" stopOpacity="0.42" />
            <stop offset="100%" stopColor="#f87171" stopOpacity="0" />
          </linearGradient>
        </defs>
        <g fill="none" stroke="url(#platformLoaderWave)" strokeLinecap="round" strokeWidth="1.15">
          <path d="M-90 164 C120 78 250 100 418 174 C610 258 748 214 922 174 C1110 132 1214 54 1530 116" opacity="0.48" />
          <path d="M-90 184 C130 102 260 122 424 188 C610 264 758 226 930 190 C1122 150 1226 78 1530 136" opacity="0.40" />
          <path d="M-90 204 C140 126 270 144 430 202 C612 270 770 238 938 206 C1132 168 1238 102 1530 156" opacity="0.32" />
          <path d="M-90 224 C150 150 282 168 438 218 C616 278 780 252 946 222 C1142 186 1250 128 1530 178" opacity="0.24" />
          <path d="M-90 244 C160 176 294 194 448 236 C624 286 792 266 958 240 C1152 208 1264 154 1530 202" opacity="0.18" />
        </g>
        <g fill="none" stroke="url(#platformLoaderWave)" strokeLinecap="round" strokeWidth="0.85">
          <path d="M-70 122 C126 170 252 208 438 186 C604 166 764 104 942 140 C1118 174 1262 238 1510 186" opacity="0.22" />
          <path d="M-70 144 C138 188 264 220 446 200 C612 182 778 126 952 158 C1128 190 1274 252 1510 206" opacity="0.18" />
        </g>
        <g fill="#f472b6" opacity="0.22">
          <circle cx="120" cy="122" r="1.8" />
          <circle cx="1280" cy="112" r="2.4" />
          <circle cx="1336" cy="184" r="1.5" />
        </g>
      </svg>

      <div className="relative z-10 w-full max-w-2xl px-6 text-center">
        {/* Formal CHG medallion: visual-only progress treatment, no auth flow change. */}
        <div className="relative mx-auto mb-12 h-44 w-44">
          <div className="absolute inset-0 rounded-full bg-white/[0.03] shadow-[0_30px_90px_rgba(19,11,21,0.45)] backdrop-blur-sm"></div>
          <div className="absolute inset-3 rounded-full border border-white/10"></div>
          <div className="absolute inset-7 rounded-full border border-primary-200/10"></div>
          <svg className="absolute inset-0 h-full w-full -rotate-90" viewBox="0 0 180 180" aria-hidden="true">
            <defs>
              <linearGradient id="platformLoaderRing" x1="25" x2="155" y1="25" y2="155">
                <stop offset="0%" stopColor="#fbcfe8" />
                <stop offset="54%" stopColor="#8d3165" />
                <stop offset="100%" stopColor="#722f37" />
              </linearGradient>
            </defs>
            <circle
              cx="90"
              cy="90"
              r={ringRadius}
              fill="none"
              stroke="rgba(255,255,255,0.10)"
              strokeWidth="1.5"
            />
            <circle
              cx="90"
              cy="90"
              r={ringRadius}
              fill="none"
              stroke="url(#platformLoaderRing)"
              strokeDasharray={ringCircumference}
              strokeDashoffset={ringOffset}
              strokeLinecap="round"
              strokeWidth="4"
              className="transition-all duration-300 ease-out"
            />
          </svg>
          <div
            className="absolute inset-10 rounded-full border border-white/[0.12] bg-gradient-to-br from-white/[0.12] to-white/[0.035] shadow-[inset_0_1px_0_rgba(255,255,255,0.14),0_18px_55px_rgba(0,0,0,0.25)] animate-pulse"
            style={{ animationDuration: '3.4s' }}
          ></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-2xl font-semibold tracking-[0.2em] text-white drop-shadow-lg">CHG</span>
          </div>
        </div>

        <div className="mb-8 flex items-center justify-center gap-3 text-primary-100/95 text-base sm:text-lg">
          <LoaderStageIcon className="h-5 w-5 text-primary-200" aria-hidden="true" />
          <p>{loaderMessage}</p>
        </div>

        {/* Visual-only loading flow replaces the bar while preserving progress semantics. */}
        <div
          className="mx-auto w-full max-w-sm"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={progress}
        >
          <div className="flex items-center justify-center gap-3 sm:gap-4">
            <Database
              className="h-9 w-9 shrink-0 text-primary-200 drop-shadow-[0_0_14px_rgba(244,114,182,0.45)]"
              strokeWidth={1.8}
              aria-hidden="true"
            />
            <div className="relative h-px w-16 rounded-full bg-white/[0.14] sm:w-20">
              <div
                className="h-full rounded-full bg-gradient-to-r from-primary-200 to-primary-400 shadow-[0_0_12px_rgba(244,114,182,0.45)] transition-all duration-300"
                style={{ width: `${dataConnectorProgress}%` }}
              ></div>
              <span
                className="absolute top-1/2 h-1.5 w-1.5 -translate-y-1/2 rounded-full bg-primary-200 shadow-[0_0_10px_rgba(244,114,182,0.75)] transition-all duration-300"
                style={{ left: `calc(${dataConnectorProgress}% - 3px)` }}
              ></span>
            </div>
            <Cloud
              className={`h-12 w-12 shrink-0 transition-colors duration-300 ${
                isPlatformStage ? 'text-primary-100 drop-shadow-[0_0_14px_rgba(244,114,182,0.26)]' : 'text-white/28'
              }`}
              strokeWidth={1.6}
              aria-hidden="true"
            />
            <div className="relative h-px w-16 rounded-full bg-white/[0.14] sm:w-20">
              <div
                className="h-full rounded-full bg-gradient-to-r from-primary-300 to-secondary-400 shadow-[0_0_12px_rgba(248,113,113,0.32)] transition-all duration-300"
                style={{ width: `${platformConnectorProgress}%` }}
              ></div>
              {progress >= 40 && (
                <span
                  className="absolute top-1/2 h-1.5 w-1.5 -translate-y-1/2 rounded-full bg-primary-200 shadow-[0_0_10px_rgba(244,114,182,0.75)] transition-all duration-300"
                  style={{ left: `calc(${platformConnectorProgress}% - 3px)` }}
                ></span>
              )}
            </div>
            <div
              className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border transition-all duration-300 ${
                isDashboardStage
                  ? 'border-primary-200/55 text-primary-100 shadow-[0_0_18px_rgba(244,114,182,0.22)]'
                  : 'border-white/16 text-white/30'
              }`}
            >
              <BarChart3 className="h-7 w-7" strokeWidth={1.7} aria-hidden="true" />
            </div>
          </div>
          <p className="mt-5 text-xs text-primary-100/65">Loading system components...</p>
        </div>
      </div>
    </div>
  );
};

// App loading screen
export const AppLoadingScreen: React.FC = () => <PlatformLoader />;

// Main Section Loader - For content areas (not header/sidebar)
export const MainSectionLoader: React.FC<{ message?: string; height?: string }> = ({
  message = "Loading content...",
  height = "min-h-[400px]"
}) => (
  <div className={`${height} bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center rounded-2xl border border-slate-200`}>
    <div className="text-center">
      {/* Modern animated loader */}
      <div className="relative mb-6">
        <div className="w-14 h-14 border-3 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
        <div className="absolute inset-0 w-14 h-14 border-2 border-pink-400/30 rounded-full animate-ping"></div>
      </div>

      <h3 className="text-xl font-semibold text-slate-800 mb-2">Loading Section</h3>
      <p className="text-slate-600">{message}</p>

      {/* Subtle progress dots */}
      <div className="flex justify-center space-x-1 mt-4">
        <div className="w-2 h-2 bg-purple-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
        <div className="w-2 h-2 bg-pink-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
        <div className="w-2 h-2 bg-purple-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
      </div>
    </div>
  </div>
);

// Tab loading screen
export const TabLoadingScreen: React.FC = () => <MainSectionLoader />;

// Compact Content Loader - For inline content areas
export const ContentLoader: React.FC<{ message?: string; size?: 'sm' | 'md' | 'lg' }> = ({
  message,
  size = 'md'
}) => {
  const containerClasses = {
    sm: 'py-4',
    md: 'py-8',
    lg: 'py-12'
  };

  return (
    <div className={`flex flex-col items-center justify-center ${containerClasses[size]}`}>
      <div className="relative">
        <div className="w-8 h-8 border-2 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
        <div className="absolute inset-0 w-8 h-8 border border-pink-400/30 rounded-full animate-ping"></div>
      </div>
      {message && <p className="text-sm text-slate-600 mt-3">{message}</p>}
    </div>
  );
};

// Simple Loading Spinner - Enhanced with platform colors
export const LoadingSpinner: React.FC<{ size?: 'sm' | 'md' | 'lg'; color?: string }> = ({
  size = 'md',
  color = 'text-purple-600'
}) => {
  const sizeClasses = {
    sm: 'w-4 h-4 border-2',
    md: 'w-6 h-6 border-2',
    lg: 'w-8 h-8 border-3'
  };

  return (
    <div className={`${sizeClasses[size]} ${color} border-current border-t-transparent rounded-full animate-spin`} />
  );
};

// Skeleton Loading Components - For specific section layouts
export const SkeletonLoader: React.FC<{
  rows?: number;
  columns?: number;
  height?: string;
  className?: string;
}> = ({
  rows = 3,
  columns = 1,
  height = 'h-4',
  className = ''
}) => (
  <div className={`space-y-3 ${className}`}>
    {Array.from({ length: rows }, (_, rowIndex) => (
      <div key={rowIndex} className="flex space-x-3">
        {Array.from({ length: columns }, (_, colIndex) => (
          <div
            key={colIndex}
            className={`${height} bg-slate-200 rounded-lg animate-pulse flex-1`}
          />
        ))}
      </div>
    ))}
  </div>
);

// Card Grid Skeleton - Generic for any card-based layouts
export const CardGridSkeleton: React.FC<{ count?: number; className?: string }> = ({
  count = 6,
  className = ''
}) => (
  <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 ${className}`}>
    {Array.from({ length: count }, (_, i) => (
      <div key={i} className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
        <div className="flex items-center justify-between mb-4">
          <div className="h-6 bg-slate-200 rounded-lg w-32 animate-pulse"></div>
          <div className="h-4 bg-slate-200 rounded-lg w-16 animate-pulse"></div>
        </div>
        <SkeletonLoader rows={3} />
      </div>
    ))}
  </div>
);

// Modal Loading Screen
export const ModalLoadingScreen: React.FC = () => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white rounded-2xl p-8 shadow-2xl max-w-md w-full mx-4">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-pink-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <h3 className="text-lg font-semibold text-slate-800 mb-2">Processing...</h3>
        <p className="text-slate-600">Please wait while we process your request.</p>
      </div>
    </div>
  </div>
);

// Button Loading State
export const LoadingButton: React.FC<{
  children: React.ReactNode;
  loading: boolean;
  onClick?: () => void;
  className?: string;
  disabled?: boolean;
}> = ({ children, loading, onClick, className = '', disabled = false }) => (
  <button
    onClick={onClick}
    disabled={loading || disabled}
    className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-all ${
      loading || disabled 
        ? 'bg-slate-300 text-slate-500 cursor-not-allowed' 
        : className
    }`}
  >
    {loading && <LoadingSpinner size="sm" />}
    {children}
  </button>
);

// Data Loading State - Now uses ContentLoader
export const DataLoadingState: React.FC<{ message?: string }> = ({ 
  message = "Loading data..." 
}) => <ContentLoader message={message} size="lg" />;

// Enhanced Empty State with Loading - More flexible and reusable
export const EmptyStateWithLoader: React.FC<{
  icon: React.ReactNode;
  title: string;
  description: string;
  size?: 'sm' | 'md' | 'lg';
}> = ({ icon, title, description, size = 'md' }) => {
  const sizeClasses = {
    sm: 'py-8',
    md: 'py-12',
    lg: 'py-16'
  };

  const iconSizes = {
    sm: 'w-12 h-12',
    md: 'w-16 h-16',
    lg: 'w-20 h-20'
  };

  return (
    <div className={`text-center ${sizeClasses[size]} bg-white/80 backdrop-blur-sm rounded-2xl border border-slate-200`}>
      <div className={`${iconSizes[size]} bg-gradient-to-br from-slate-100 to-slate-200 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-sm`}>
        {icon}
      </div>
      <h3 className="text-lg font-semibold text-slate-800 mb-2">{title}</h3>
      <p className="text-slate-600 mb-6 max-w-md mx-auto">{description}</p>
      <ContentLoader size="sm" />
    </div>
  );
};

// Quick access functions for common empty loading states
export const createEmptyLoader = (icon: React.ReactNode, title: string, description: string) =>
  () => <EmptyStateWithLoader icon={icon} title={title} description={description} />;

export const AnalyticsEmptyLoading = createEmptyLoader(
  <BarChart3 className="w-8 h-8 text-slate-400" />,
  "Loading Intelligence",
  "Preparing charts and performance metrics..."
);

export const UsersEmptyLoading = createEmptyLoader(
  <User className="w-8 h-8 text-slate-400" />,
  "Loading Users",
  "Fetching user management data..."
);
